import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  listClientsFull,
  listCustomFieldSchemas,
  type ClientListQuery,
} from "@/api/lookups";
import type { ClientResponse, CustomFieldSchemaResponse } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/table/DataTable";
import { useCan } from "@/hooks/useCan";
import { useFolders } from "@/hooks/useFolders";
import { ClientFormDialog } from "@/components/registries/ClientFormDialog";

interface ClientFilters {
  search: string;
  custom_field_key: string;
  custom_field_value: string;
}

const EMPTY: ClientFilters = {
  search: "",
  custom_field_key: "",
  custom_field_value: "",
};

function toQuery(f: ClientFilters): ClientListQuery {
  const q: ClientListQuery = { limit: 100 };
  if (f.search) q.search = f.search;
  if (f.custom_field_key) q.custom_field_key = f.custom_field_key;
  if (f.custom_field_value) q.custom_field_value = f.custom_field_value;
  return q;
}

export function ClientsRegistry() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canCreate = useCan("client_crud");
  const [filters, setFilters] = useState<ClientFilters>(EMPTY);
  const [draft, setDraft] = useState<ClientFilters>(EMPTY);
  const [creating, setCreating] = useState(false);

  // TM-035ae774: a row click opens the client card (editing lives there)
  // instead of the inline edit dialog. `clientId` matches the route in
  // `src/routes/clients.$clientId.tsx`.
  const goToClient = useCallback(
    (row: ClientResponse) => {
      void navigate({
        to: "/clients/$clientId",
        params: { clientId: row.id },
      });
    },
    [navigate],
  );
  // Papki-tenants v1: folder scope drives the clients query key + query
  // string. Keying on `activeFolderId` makes the cache entry unique per
  // folder so a folder switch produces a fresh fetch instead of replaying
  // a stale cross-folder list.
  const { activeFolderId } = useFolders();

  const clients = useQuery({
    queryKey: ["clients", "list", filters, activeFolderId],
    queryFn: () => listClientsFull(toQuery(filters), activeFolderId),
  });
  const schemas = useQuery({
    queryKey: ["custom-fields", "client"],
    queryFn: () => listCustomFieldSchemas("client"),
  });

  const columns = useColumns(schemas.data ?? []);

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">
            {t("clients.registryTitle")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("clients.registrySubtitle")}
          </p>
        </div>
        {canCreate ? (
          <Button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="open-create-client"
          >
            {t("clients.createButton")}
          </Button>
        ) : null}
      </header>

      <form
        className="grid grid-cols-1 gap-2 md:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          setFilters(draft);
        }}
        data-testid="clients-filters"
      >
        <Input
          aria-label={t("common.search")}
          placeholder={t("common.search")}
          value={draft.search}
          onChange={(e) => setDraft((d) => ({ ...d, search: e.target.value }))}
        />
        <select
          aria-label={t("adminCustomFields.fields.key")}
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={draft.custom_field_key}
          onChange={(e) =>
            setDraft((d) => ({ ...d, custom_field_key: e.target.value }))
          }
        >
          <option value="">{t("common.dash")}</option>
          {(schemas.data ?? [])
            .filter((s) => s.is_filterable)
            .map((s) => (
              <option key={s.id} value={s.key}>
                {s.label}
              </option>
            ))}
        </select>
        <Input
          aria-label={t("adminCustomFields.fields.label")}
          placeholder={t("adminCustomFields.fields.label")}
          value={draft.custom_field_value}
          onChange={(e) =>
            setDraft((d) => ({ ...d, custom_field_value: e.target.value }))
          }
        />
        <Button type="submit" size="sm" variant="outline">
          {t("common.filterApply")}
        </Button>
      </form>

      <DataTable
        data={clients.data ?? []}
        columns={columns}
        testId="clients-table"
        isLoading={clients.isLoading}
        onRowClick={goToClient}
      />

      <ClientFormDialog
        open={creating}
        existing={null}
        onClose={() => setCreating(false)}
      />
    </div>
  );
}

function useColumns(
  schemas: CustomFieldSchemaResponse[],
): ColumnDef<ClientResponse, unknown>[] {
  const { t } = useTranslation();
  return useMemo(() => {
    const base: ColumnDef<ClientResponse, unknown>[] = [
      {
        id: "name",
        header: t("clients.columns.name"),
        cell: (info) => info.row.original.name,
      },
      // ИНН column intentionally hidden from the registry (TM-f518b4c8).
      // The value is still stored in `ClientResponse.inn` and editable via
      // the form dialog — only the table column is suppressed.
      {
        id: "description",
        header: t("clients.columns.description"),
        cell: (info) => info.row.original.description ?? t("common.dash"),
      },
      {
        id: "aliases",
        header: t("clients.columns.aliases"),
        cell: (info) => info.row.original.aliases.join(", "),
      },
    ];
    for (const s of schemas) {
      base.push({
        id: `cf_${s.key}`,
        header: s.label,
        cell: (info) => {
          const v = info.row.original.custom_field_values[s.key];
          if (v === undefined || v === null || v === "") {
            return t("common.dash");
          }
          return String(v);
        },
      });
    }
    return base;
  }, [t, schemas]);
}
